import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User, AuthState } from '../types';
import { apiService } from '../services/api';
import { PermissionsService } from '../services/permissionsService';
import { subscribeToPushNotifications, unsubscribeFromPushNotifications } from '../services/pushNotificationService';

interface AuthContextType {
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, companyWebsite?: string) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
  refreshProfile: () => Promise<void>;
  refreshAuthToken: () => Promise<string | undefined>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGOUT' }
  | { type: 'SET_USER'; payload: User }
  | { type: 'AUTH_VERIFIED'; payload: User }
  | { type: 'AUTH_INIT_COMPLETE' };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return {
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
      };
    case 'LOGOUT':
      return {
        user: null,
        token: null,
        isAuthenticated: false,
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'AUTH_VERIFIED':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        loading: false,
      };
    case 'AUTH_INIT_COMPLETE':
      return {
        ...state,
        loading: false,
      };
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false, // Will be set to true after token verification
  loading: true, // Loading state for auth initialization
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      console.log('🔐 AuthContext: Verifying existing token...');
      // Use getProfile which returns complete user data including role and permissions
      apiService.getProfile()
        .then(profile => {
          console.log('🔐 AuthContext: Token verified, profile loaded:', profile);
          dispatch({ type: 'AUTH_VERIFIED', payload: {
            id: profile.id || '',
            name: profile.name || '',
            email: profile.email || '',
            avatar: profile.avatar,
            role: profile.role || 'member',
            permissions: profile.permissions || PermissionsService.getDefaultPermissions(profile.role || 'member'),
            createdAt: profile.createdAt || '',
            updatedAt: profile.updatedAt || ''
          }});
        })
        .catch((error) => {
          console.error('🔐 AuthContext: Token verification failed:', error);
          // Token is invalid, remove it
          localStorage.removeItem('token');
          dispatch({ type: 'LOGOUT' });
        })
        .finally(() => {
          // Mark auth initialization as complete
          dispatch({ type: 'AUTH_INIT_COMPLETE' });
        });
    } else {
      console.log('🔐 AuthContext: No token found, user not authenticated');
      // No token, mark initialization complete immediately
      dispatch({ type: 'AUTH_INIT_COMPLETE' });
    }
  }, []);

  // Set up periodic profile refresh to catch permission changes
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const profileRefreshInterval = setInterval(() => {
      console.log('🔄 AuthContext: Performing periodic profile refresh...');
      refreshProfile().catch(error => {
        console.error('🔄 AuthContext: Periodic profile refresh failed:', error);
      });
    }, 5 * 60 * 1000); // Refresh every 5 minutes

    return () => {
      clearInterval(profileRefreshInterval);
    };
  }, [state.isAuthenticated]);

  // Set up immediate profile refresh when user becomes active or window gains focus
  useEffect(() => {
    if (!state.isAuthenticated) return;

    let lastRefresh = Date.now();
    const MIN_REFRESH_INTERVAL = 30 * 1000; // Don't refresh more than once every 30 seconds

    const handleVisibilityChange = () => {
      if (!document.hidden && Date.now() - lastRefresh > MIN_REFRESH_INTERVAL) {
        console.log('🔄 AuthContext: Window became visible, checking for profile updates...');
        lastRefresh = Date.now();
        refreshProfile().catch(error => {
          console.error('🔄 AuthContext: Visibility-triggered profile refresh failed:', error);
        });
      }
    };

    const handleFocus = () => {
      if (Date.now() - lastRefresh > MIN_REFRESH_INTERVAL) {
        console.log('🔄 AuthContext: Window gained focus, checking for profile updates...');
        lastRefresh = Date.now();
        refreshProfile().catch(error => {
          console.error('🔄 AuthContext: Focus-triggered profile refresh failed:', error);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [state.isAuthenticated]);

  const login = async (email: string, password: string) => {
    try {
      const { user, token } = await apiService.login(email, password);
      localStorage.setItem('token', token);
      
      // Get complete profile data including permissions
      try {
        const profile = await apiService.getProfile();
        console.log('🔐 AuthContext: Profile loaded after login:', profile);
        const completeUser = {
          id: profile.id || user.id,
          name: profile.name || user.name,
          email: profile.email || user.email,
          avatar: profile.avatar,
          role: profile.role || user.role || 'member',
          permissions: profile.permissions || user.permissions || PermissionsService.getDefaultPermissions(profile.role || user.role || 'member'),
          createdAt: profile.createdAt || user.createdAt || '',
          updatedAt: profile.updatedAt || user.updatedAt || ''
        };
        dispatch({ type: 'LOGIN_SUCCESS', payload: { user: completeUser, token } });
        // Subscribe to Web Push (fire-and-forget)
        subscribeToPushNotifications(token).catch(() => {});
      } catch (profileError) {
        console.error('🔐 AuthContext: Failed to load profile after login, using login data:', profileError);
        dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
        // Subscribe to Web Push (fire-and-forget)
        subscribeToPushNotifications(token).catch(() => {});
      }
    } catch (error) {
      throw error;
    }
  };

  const register = async (name: string, email: string, password: string, companyWebsite?: string) => {
    try {
      const { user, token } = await apiService.register(name, email, password, companyWebsite);
      localStorage.setItem('token', token);
      dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    // Unsubscribe push before clearing token (fire-and-forget)
    const currentToken = localStorage.getItem('token');
    if (currentToken) {
      unsubscribeFromPushNotifications(currentToken).catch(() => {});
    }
    localStorage.removeItem('token');
    apiService.logout();
    dispatch({ type: 'LOGOUT' });
  };

  const updateUser = (user: User) => {
    dispatch({ type: 'SET_USER', payload: user });
  };

  const refreshProfile = async () => {
    try {
      const profile = await apiService.getProfile();
      dispatch({ type: 'SET_USER', payload: {
        id: profile.id || state.user?.id || '',
        name: profile.name || state.user?.name || '',
        email: profile.email || state.user?.email || '',
        avatar: profile.avatar || state.user?.avatar,
        role: profile.role || 'member',
        permissions: profile.permissions || PermissionsService.getDefaultPermissions(profile.role || 'member'),
        createdAt: profile.createdAt || state.user?.createdAt || '',
        // eslint-disable-next-line no-restricted-syntax
        updatedAt: profile.updatedAt || new Date().toISOString()
      }});
    } catch (error) {
      console.error('🔄 AuthContext: Failed to refresh profile:', error);
      throw error;
    }
  };

  const refreshAuthToken = async () => {
    try {
      console.log('🔄 AuthContext: Refreshing auth token...');
      const response = await apiService.refreshToken();
      
      if (response.token) {
        localStorage.setItem('token', response.token);
        console.log('✅ AuthContext: Token refreshed successfully');
        
        // Update token in state
        dispatch({ 
          type: 'LOGIN_SUCCESS', 
          payload: { 
            user: state.user!, 
            token: response.token 
          } 
        });
        
        return response.token;
      }
    } catch (error) {
      console.error('❌ AuthContext: Token refresh failed:', error);
      // On refresh failure, logout user
      logout();
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ state, login, register, logout, updateUser, refreshProfile, refreshAuthToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
